<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="apply-modal-title" role="dialog" aria-modal="true">
      <!-- Background overlay -->
      <div 
        class="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm transition-opacity"
        @click="closeModal"
      ></div>
      
      <!-- Modal panel -->
      <div class="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
        <div 
          class="relative transform overflow-hidden rounded-2xl bg-gradient-to-br from-[#10103c] to-[#141450] text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
          @click.stop
        >
          <!-- Modal content -->
          <div class="bg-gradient-to-b from-purple-900/10 to-purple-900/20 p-8 text-white">
            <!-- Close button -->
            <button 
              @click="closeModal" 
              class="absolute top-4 right-4 rounded-full p-1 text-white/80 hover:text-white focus:outline-none transition-colors"
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <!-- Header -->
            <div class="mb-8 text-center">
              <div class="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 id="apply-modal-title" class="text-3xl font-heading mb-3 text-white">{{ applyTitle }}</h3>
            </div>
            
            <!-- Content -->
            <div class="mb-8">
              <div class="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 p-6 mb-6">
                <p class="text-white/90 font-montserrat leading-relaxed text-center">
                  {{ applyContent }}
                </p>
              </div>

              <!-- Email info with copy button -->
              <div class="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg border border-blue-500/30 p-4 mb-6">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <p class="text-white/70 text-sm mb-1">{{ emailLabel }}</p>
                    <p class="text-white font-semibold">nancy@rendery.org</p>
                    <p class="text-white/70 text-sm mt-1">{{ subjectLabel }} "Rendery"</p>
                  </div>
                  <button
                    @click="copyEmailInfo"
                    class="ml-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white font-medium transition-all duration-200 flex items-center gap-2 border border-white/20"
                  >
                    <svg v-if="!copied" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <svg v-else class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {{ copied ? copiedText : copyText }}
                  </button>
                </div>
              </div>

              <!-- Success message for copy -->
              <div v-if="copied" class="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                <p class="text-green-300 text-sm text-center">{{ copySuccessText }}</p>
              </div>
            </div>

            <!-- Footer link -->
            <div class="text-center pt-4 border-t border-white/10">
              <button
                @click="openSignUpModal"
                class="text-blue-400 hover:text-blue-300 underline font-medium transition-colors"
              >
                {{ alreadyHaveCodeText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import emitter from '@/eventBus';
import faqData from '../../lang.json';

export default {
  name: 'ApplyModal',
  data() {
    return {
      isOpen: false,
      selectedLanguage: 'en',
      copied: false,
      copyTimeout: null
    }
  },
  computed: {
    applyTitle() {
      return faqData.applyModal?.[this.selectedLanguage]?.title || faqData.applyModal?.['en']?.title || 'Want to get started?';
    },
    applyContent() {
      return faqData.applyModal?.[this.selectedLanguage]?.content || faqData.applyModal?.['en']?.content || 
        'Send an email to nancy@rendery.org using the email listed on your channel\'s page with the subject "Rendery" - it only takes 5 minutes to get onboarded to the new AI data economy!';
    },
    emailLabel() {
      return faqData.applyModal?.[this.selectedLanguage]?.emailLabel || faqData.applyModal?.['en']?.emailLabel || 'Email:';
    },
    subjectLabel() {
      return faqData.applyModal?.[this.selectedLanguage]?.subjectLabel || faqData.applyModal?.['en']?.subjectLabel || 'Subject:';
    },
    copyText() {
      return faqData.applyModal?.[this.selectedLanguage]?.copyText || faqData.applyModal?.['en']?.copyText || 'Copy';
    },
    copiedText() {
      return faqData.applyModal?.[this.selectedLanguage]?.copiedText || faqData.applyModal?.['en']?.copiedText || 'Copied!';
    },
    copySuccessText() {
      return faqData.applyModal?.[this.selectedLanguage]?.copySuccessText || faqData.applyModal?.['en']?.copySuccessText || 'Email details copied to clipboard!';
    },
    alreadyHaveCodeText() {
      return faqData.applyModal?.[this.selectedLanguage]?.alreadyHaveCodeText || faqData.applyModal?.['en']?.alreadyHaveCodeText || 
        'Already have an invitation access code? Click here to get started';
    }
  },
  mounted() {
    // Listen for language change events
    emitter.on('change-language', (langCode) => {
      this.selectedLanguage = langCode;
    });
    
    // Listen for open modal event
    emitter.on('open-apply-modal', () => {
      this.isOpen = true;
      document.body.classList.add('overflow-hidden');
    });
    
    // Check for stored language preference
    const storedLang = localStorage.getItem('preferredLanguage');
    if (storedLang) {
      this.selectedLanguage = storedLang;
    }
  },
  beforeUnmount() {
    emitter.off('change-language');
    emitter.off('open-apply-modal');
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  },
  methods: {
    closeModal() {
      this.isOpen = false;
      this.copied = false;
      document.body.classList.remove('overflow-hidden');
    },
    async copyEmailInfo() {
      const emailText = `To: nancy@rendery.org\nSubject: Rendery\n\nHi Nancy,\n\nI would like to join the Rendery Consortium as a YouTube creator. Please let me know the next steps.\n\nBest regards`;
      
      try {
        await navigator.clipboard.writeText(emailText);
        this.copied = true;
        
        // Reset copied state after 3 seconds
        if (this.copyTimeout) {
          clearTimeout(this.copyTimeout);
        }
        this.copyTimeout = setTimeout(() => {
          this.copied = false;
        }, 3000);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        // Fallback for older browsers
        this.fallbackCopyTextToClipboard(emailText);
      }
    },
    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';
      
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.copied = true;
        
        if (this.copyTimeout) {
          clearTimeout(this.copyTimeout);
        }
        this.copyTimeout = setTimeout(() => {
          this.copied = false;
        }, 3000);
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
      }
      
      document.body.removeChild(textArea);
    },
    openSignUpModal() {
      // Close this modal first
      this.closeModal();
      
      // Then open the sign up modal
      setTimeout(() => {
        emitter.emit('open-signup-modal');
      }, 100);
    }
  }
}
</script>

<style scoped>
.animate-gradient-slow {
  animation: gradient-slow 3s ease infinite;
  background-size: 200% 200%;
}

@keyframes gradient-slow {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style> 